import ClickAwayListener from '@components/App/ClickAwayListener/ClickAwayListener'
import useOnDragStart from '@components/ArtBoard/DragAndDrop/hooks/useOnDragStart'
import { DRAGGABLE_TYPE } from '@components/ArtBoard/DragAndDrop/types'
import {
  type BlockWireframe,
  findColorByHex,
  type ID,
  type LibrarySymbol as LibrarySymbolItem,
} from '@octopus-do/octopus-project-core'
import {
  thunkRemoveSymbolFromLibrary,
  thunkUpdateLibrarySymbol,
  thunkUpdateLibrarySymbolColor,
  thunkUploadLibrarySymbolImage,
} from '@store/migrationActions/thunkActions/thunkSymbolActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import type React from 'react'
import { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { COLOR_BLUE } from '@/constants/constants'
import { isInsidePopover } from '@/utils'
import { SymbolCard } from '../../../SymbolCard'
import { SymbolToolbar } from '../../../SymbolToolbar'

type Props = {
  item: LibrarySymbolItem
  index: number
  overlay: boolean
  selected: boolean
  /** The name is being typed: right after creation or on the second click. */
  editing: boolean
  canEdit: boolean
  onSelect: (id: ID | null) => void
  onEdit: (id: ID | null) => void
}

/** Marks the drag as coming from the library: the drop pulls the item first. */
export const LIBRARY_DRAG_DATA = { library: true }

/** A library symbol in the sidebar: the item is the source, the edits go to the library. */
export const LibrarySymbol: React.FC<Props> = ({
  item,
  index,
  overlay,
  selected,
  editing,
  canEdit,
  onSelect,
  onEdit,
}) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()
  const ref = useRef<HTMLDivElement>(null)

  const colors = useSelector((state: AppState) => state.manager.project.colors)

  const { title, visual, color: libraryColor } = item.data
  // The library keeps the plain hex; the card shows it the way the project theme does.
  const color = useSelector(
    migrationProjectSelector.getHexForUI(libraryColor?.hexCode ?? COLOR_BLUE)
  )
  // The picker speaks palette ids: the entry with the library hex, if any.
  const colorId = findColorByHex(colors, libraryColor?.hexCode ?? '')?.id ?? ''

  const onDragStart = useOnDragStart(ref, DRAGGABLE_TYPE.SYMBOL, item.id, {
    ...LIBRARY_DRAG_DATA,
    name: title,
  })

  // Like a project symbol: the first click selects, the next one edits the name.
  const onClick = () => {
    if (selected && canEdit) {
      onEdit(item.id)
      return
    }

    onSelect(item.id)
  }

  const onRename = (name: string) => {
    onEdit(null)

    if (name !== title) {
      dispatch(
        thunkUpdateLibrarySymbol({ libraryId: item.id, patch: { title: name } })
      )
    }
  }

  const onColor = (nextColorId: ID) => {
    dispatch(
      thunkUpdateLibrarySymbolColor({
        libraryId: item.id,
        colorId: nextColorId,
      })
    )
  }

  const onWireframesChange = (wireframes: BlockWireframe[]) => {
    dispatch(
      thunkUpdateLibrarySymbol({
        libraryId: item.id,
        patch: { visual: { type: 'wireframes', wireframes } },
      })
    )
  }

  const onImage = async (file: File) => {
    try {
      await dispatch(
        thunkUploadLibrarySymbolImage({ libraryId: item.id, file })
      )
    } catch (error) {
      console.error('upload image:', error)
    }
  }

  const onRemoveImage = () => {
    dispatch(
      thunkUpdateLibrarySymbol({
        libraryId: item.id,
        patch: { visual: { type: 'wireframes', wireframes: [] } },
      })
    )
  }

  const onDelete = () => {
    dispatch(thunkRemoveSymbolFromLibrary({ libraryId: item.id }))
  }

  // The toolbar's menus render in a portal outside the card.
  const onClickAway = (e: MouseEvent) => {
    if (selected && !isInsidePopover(e.target)) {
      onSelect(null)
    }
  }

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <SymbolCard
        id={item.id}
        index={index}
        dragData={LIBRARY_DRAG_DATA}
        componentRef={ref}
        title={title}
        color={color}
        visual={visual}
        editing={editing}
        highlighted={selected}
        overlay={overlay}
        onDragStart={onDragStart}
        onClick={onClick}
        onRename={onRename}
        onSubmit={() => onSelect(null)}
      >
        {selected && !overlay && !editing && canEdit && (
          <SymbolToolbar
            anchorRef={ref}
            colorId={colorId}
            hasImage={visual.type === 'image'}
            deleteLabel="Remove from library"
            onColor={onColor}
            onWireframesChange={onWireframesChange}
            onImage={onImage}
            onRemoveImage={onRemoveImage}
            onDelete={onDelete}
            wireframes={visual.type === 'wireframes' ? visual.wireframes : []}
          />
        )}
      </SymbolCard>
    </ClickAwayListener>
  )
}
